import {
  ModalProps,
  createUseModal,
  useFormModal,
} from "@imminently/react/context/modals";
import { Button } from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { DismissIcon } from "@/assets/icons";
import MultilineTextInput from "@/components/MultilineTextInput";
import PatientCardModal from "@/components/PatientCardModal";
import { PatientEncounter } from "@/resources/types";
import { encounterService } from "@/services";

const { useUpdate } = encounterService;

const schema = yup.object().shape({
  note: yup.string().required("Distress note is required"),
});

interface DismissPatientDistresstModalProps {
  encounter: PatientEncounter;
}

const DismissPatientDistresstModal = ({ props }: ModalProps<DismissPatientDistresstModalProps>) => {
  const { encounter } = props;

  const { mutateAsync, isLoading } = useUpdate();
  const { methods, notify, close } = useFormModal({
    mode: "onSubmit",
    defaultValues: {
      note: "",
    },
    resolver: yupResolver(schema),
  });

  const { control, handleSubmit, formState } = methods;

  const submit = (data: any) => {
    const note = { text: data.note };

    // Dismiss distress, submit note
    mutateAsync({
      id: encounter.id,
      payload: { distressed: false, note },
    }).catch(() => {
      notify.error("Failed to dismiss distress");
    });

    close(true);
  };

  return (
    <PatientCardModal
      title="Dismiss distress"
      encounter={encounter}
      onClose={() => close()}
    >
      <MultilineTextInput
        control={control}
        field="note"
        required
        label="Notes..."
        rows={6}
      />

      <Button
        sx={{ ml: "auto" }}
        disabled={isLoading || !formState.isValid}
        color="success"
        startIcon={<DismissIcon />}
        onClick={handleSubmit(submit)}
      >
        Dismiss distress
      </Button>
    </PatientCardModal>
  );
};

export const useDismissPatientDistresstModal = createUseModal(
  DismissPatientDistresstModal
);
